import React, { FunctionComponent, memo, useCallback } from "react";
import { Grid, Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Input, Singleselect, ListOptionBase } from "@shared/components";
import FieldTooltip from "@investorRequest/components/FieldTooltip";
import { FIELD_TITLES } from "@investorRequest/constants/fieldTooltips";
import InvestorRequestTextarea from "@investorRequest/components/InvestorRequestTextarea";
import InvestorRequestDatepicker from "@investorRequest/components/InvestorRequestDatepicker";
import FurtherCreditFields from "@investorRequest/components/FurtherCreditFields";
import { validationMessages } from "@investorRequest/constants/validationMessages";
import { fieldKeys } from "@investorRequest/constants/fieldKeys";
import { useDropdownOverflowTooltip } from "@shared/hooks/useDropdownOverflowTooltip";
import { getDropdownWithIconStyles } from "@shared/styles/dropdownWithIconStyles";
import { useC2DSErrors } from "@investorRequest/hooks/useC2DSFieldError";
import C2DSFieldErrorMessages from "@investorRequest/components/C2DSFieldErrorMessages";
import { Nullable } from "@shared/models/general";
import {
  isValidRoutingNumber,
  isValidExternalRoutingNumber,
  isValidAccountNumber,
  sanitizeAsciiName,
  sanitizeDigits,
  sanitizeAccountNumberInput,
} from "../utils/SpecificValidations";

const useStyles = makeStyles((theme) => getDropdownWithIconStyles(theme));

interface Props {
  values: {
    bankName: string;
    beneficiaryAccountName: string;
    bankCountryLabel?: string;
    bankAccountNumber: string;
    routingNumber: string;
    effectiveDate?: string;
    furtherCreditYesNo?: string;
    furtherCreditLine1?: string;
    furtherCreditLine2?: string;
    fundType?: string;
  };
  countriesOptions: ListOptionBase[];
  selectedCountry?: ListOptionBase;
  onSelectCountry(option: ListOptionBase): void;
  onChange(field: string, value: string): void;
  showErrors: boolean;
  inputXS: 12 | 6;
  disableBankCountry?: boolean;
}

const InvestorRequestAchFields: FunctionComponent<Props> = ({
  values,
  countriesOptions,
  selectedCountry,
  onSelectCountry,
  onChange,
  showErrors,
  inputXS,
  disableBankCountry = false,
}) => {
  const classes = useStyles();
  const { selectedValueRef, dropdownOpen, isToggleOverflowing, selectKey, handleSelect, handleToggleButtonClick } =
    useDropdownOverflowTooltip({
      testId: "bank-country-select",
      selectedLabel: selectedCountry?.label,
    });

  // LPC-10289: C2DS validation errors
  const { hasFieldError, clearFieldError } = useC2DSErrors();

  // LPC-10289: Cache the C2DS error state for routing number to ensure consistency
  const hasRoutingNumberC2DSError = hasFieldError("routingNumber");

  const handleDatepickerChange = useCallback(
    (value: Nullable<Date>) => onChange("effectiveDate", value ? value.toISOString() : ""),
    [onChange]
  );

  return (
    <>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BeneficiaryAccountName}>
          <Input
            dense={false}
            placeholder="*Beneficiary Account Name"
            variant="light"
            size="large"
            // max 140
            value={values.beneficiaryAccountName}
            onChange={(v) => onChange(fieldKeys.BeneficiaryAccountName, sanitizeAsciiName(v, 140))}
            sanitizeOnPaste={(t) => sanitizeAsciiName(t, 140)}
            error={showErrors && !(values.beneficiaryAccountName || "").trim()}
          />
        </FieldTooltip>
        {showErrors && !(values.beneficiaryAccountName || "").trim() && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {validationMessages.beneficiaryAccountName.required}
          </Box>
        )}
        {showErrors && !!(values.beneficiaryAccountName || "").trim() && values.beneficiaryAccountName.length > 140 && (
          <Box mt={1} color="warning.main" fontSize={12}>
            Max 140 characters
          </Box>
        )}
        {showErrors &&
          !!(values.beneficiaryAccountName || "").trim() &&
          values.beneficiaryAccountName.length <= 140 &&
          /[^A-Za-z0-9\s]/.test(values.beneficiaryAccountName) && (
            <Box mt={1} color="warning.main" fontSize={12}>
              {validationMessages.beneficiaryAccountName.invalid}
            </Box>
          )}
      </Grid>

      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BankName}>
          <Input
            dense={false}
            placeholder="*Beneficiary Bank Name"
            variant="light"
            size="large"
            // max 30
            value={values.bankName}
            onChange={(v) => onChange(fieldKeys.BankName, sanitizeAsciiName(v, 30))}
            sanitizeOnPaste={(t) => sanitizeAsciiName(t, 30)}
            error={showErrors && !(values.bankName || "").trim()}
          />
        </FieldTooltip>
        {showErrors && !(values.bankName || "").trim() && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {validationMessages.bankName.required}
          </Box>
        )}
        {showErrors && !!(values.bankName || "").trim() && values.bankName.length > 30 && (
          <Box mt={1} color="warning.main" fontSize={12}>
            Max 30 characters
          </Box>
        )}
        {showErrors &&
          !!(values.bankName || "").trim() &&
          values.bankName.length <= 30 &&
          /[^A-Za-z0-9\s]/.test(values.bankName) && (
            <Box mt={1} color="warning.main" fontSize={12}>
              {validationMessages.bankName.invalid}
            </Box>
          )}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.AbaRoutingNumber}>
          <Input
            dense={false}
            placeholder="*ABA / Routing #"
            variant="light"
            size="large"
            value={values.routingNumber}
            onChange={(v) => {
              // LPC-10289: Clear C2DS error when user modifies field
              clearFieldError("routingNumber");
              // For External: alphanumeric, max 15 chars
              // For Internal: numeric only, max 9 chars
              if (values.fundType === "External") {
                const sanitized = v.replace(/[^A-Za-z0-9]/g, "").slice(0, 15);

                onChange("routingNumber", sanitized);
              } else {
                onChange("routingNumber", sanitizeDigits(v, 9));
              }
            }}
            sanitizeOnPaste={(t) => {
              if (values.fundType === "External") {
                return t.replace(/[^A-Za-z0-9]/g, "").slice(0, 15);
              }

              return sanitizeDigits(t, 9);
            }}
            error={
              (showErrors &&
                (!values.routingNumber ||
                  (values.fundType === "External"
                    ? !isValidExternalRoutingNumber(values.routingNumber)
                    : !isValidRoutingNumber(values.routingNumber)))) ||
              hasRoutingNumberC2DSError
            }
          />
        </FieldTooltip>
        {showErrors &&
          (!values.routingNumber ? (
            <Box mt={1} color="warning.main" fontSize={12}>
              {validationMessages.routingNumber.required}
            </Box>
          ) : values.fundType === "External" ? (
            !isValidExternalRoutingNumber(values.routingNumber) ? (
              <Box mt={1} color="warning.main" fontSize={12}>
                Must be 9 digits or alphanumeric (max 15 characters)
              </Box>
            ) : null
          ) : !isValidRoutingNumber(values.routingNumber) ? (
            <Box mt={1} color="warning.main" fontSize={12}>
              {validationMessages.routingNumber.invalid}
            </Box>
          ) : null)}
        {/* LPC-10289: C2DS validation error */}
        <C2DSFieldErrorMessages fieldKey="routingNumber" />
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BeneficiaryBankAccountNumber}>
          <Input
            dense={false}
            placeholder="*Beneficiary Bank Account #"
            variant="light"
            size="large"
            // max 35
            value={values.bankAccountNumber}
            onChange={(v) => onChange(fieldKeys.BankAccountNumber, sanitizeAccountNumberInput(v, 35, 10))}
            sanitizeOnPaste={(t) => sanitizeAccountNumberInput(t, 35, 10)}
            error={showErrors && (!values.bankAccountNumber || !isValidAccountNumber(values.bankAccountNumber))}
          />
        </FieldTooltip>
        {showErrors &&
          (!values.bankAccountNumber ? (
            <Box mt={1} color="warning.main" fontSize={12}>
              {validationMessages.bankAccountNumber.required}
            </Box>
          ) : !isValidAccountNumber(values.bankAccountNumber) ? (
            <Box mt={1} color="warning.main" fontSize={12}>
              {validationMessages.bankAccountNumber.invalid}
            </Box>
          ) : null)}
        {showErrors && !!values.bankAccountNumber && values.bankAccountNumber.length > 35 && (
          <Box mt={1} color="warning.main" fontSize={12}>
            Max 35 characters
          </Box>
        )}
      </Grid>

      <Grid item xs={inputXS}>
        <FieldTooltip
          titleNode={
            <Box>
              <Typography>Beneficiary Bank Country</Typography>
              {isToggleOverflowing && selectedCountry?.label ? <Typography>{selectedCountry.label}</Typography> : null}
            </Box>
          }
          disableHover={dropdownOpen}
        >
          <Box className={classes.bankCountryContainer}>
            <Box className={classes.bankCountryDropdown} ref={selectedValueRef}>
              <Box>
                <Singleselect
                  key={selectKey}
                  options={countriesOptions}
                  selectedOption={selectedCountry}
                  onSelect={(option) => {
                    onSelectCountry(option);
                    handleSelect();
                  }}
                  onToggleButtonClick={handleToggleButtonClick}
                  label={selectedCountry?.label || "*Bank Country"}
                  variant="contained"
                  size="large"
                  withSearch
                  testId="bank-country-select"
                  disabled={disableBankCountry}
                />
              </Box>
            </Box>
          </Box>
        </FieldTooltip>
        {showErrors && !selectedCountry && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {validationMessages.bankCountry.required}
          </Box>
        )}
      </Grid>

      <FurtherCreditFields
        yesNoValue={values.furtherCreditYesNo || ""}
        line1={values.furtherCreditLine1 || ""}
        line2={values.furtherCreditLine2 || ""}
        onChangeYesNo={(v) => {
          onChange("furtherCreditYesNo", v);
          // IR-UI-Revamp_V1: Clear further credit fields when "No" is selected
          if (v === "no") {
            onChange("furtherCreditLine1", "");
            onChange("furtherCreditLine2", "");
          }
        }}
        onChangeLine1={(v) => onChange("furtherCreditLine1", v)}
        onChangeLine2={(v) => onChange("furtherCreditLine2", v)}
        showErrors={showErrors}
      />

      <Grid item xs={inputXS} data-testid="investor-request-datepicker">
        <FieldTooltip title={FIELD_TITLES.DateInstructionsBecomeEffective}>
          <InvestorRequestDatepicker value={values.effectiveDate || ""} onChange={handleDatepickerChange} />
        </FieldTooltip>
      </Grid>

      <Grid item xs={12}>
        <FieldTooltip title={FIELD_TITLES.AdditionalNote}>
          <InvestorRequestTextarea
            placeholder="Please include any additional notes"
            value={(values as any).freeText || ""}
            onChange={(v: string) => onChange("freeText", v)}
            error={showErrors && ((values as any).freeText || "").length > 8000}
          />
        </FieldTooltip>
        {showErrors && !!(values as any).freeText && ((values as any).freeText as string).length > 8000 && (
          <Box mt={1} color="warning.main" fontSize={12}>
            Max 8000 characters
          </Box>
        )}
      </Grid>
    </>
  );
};

export default memo(InvestorRequestAchFields);
